// src/lib/auth.ts

// 단일 헬퍼: API_BASE 합치기 + 토큰 자동 첨부 + 401 처리
const RAW_BASE = (import.meta.env.VITE_API_BASE || "").trim();

export const LS_TOKEN = "token";
export const SS_TOKEN = "token_session";

export type PersistMode = "local" | "session";
const PERSISTENT_SESSION = "julyupay_persistent_session";
const SIGNED_OUT = "julyupay_signed_out";
let sessionEpoch = 0;
let renewal: Promise<string> | null = null;

export function cancelSessionRenewal() { sessionEpoch += 1; renewal = null; }
export function hasPersistentSession() { return localStorage.getItem(PERSISTENT_SESSION) === "1"; }
export function isLoginRemembered() { return !!localStorage.getItem(LS_TOKEN); }
export function markPersistentSession() { localStorage.setItem(PERSISTENT_SESSION, "1"); }

// 예) VITE_API_BASE = "https://juluy.cafe24.com"
// 절대금지: 뒤에 /api 붙이지 마세요! (라우터에서 이미 /api 사용)
function joinUrl(base: string, path: string) {
  const b = base.replace(/\/+$/, "");
  const p = path.replace(/^\/+/, "");
  return b ? `${b}/${p}` : `/${p}`;
}

export function getToken() {
  return localStorage.getItem(LS_TOKEN) || sessionStorage.getItem(SS_TOKEN) || "";
}

export function setToken(token: string, mode: PersistMode = "local") {
  localStorage.removeItem(SIGNED_OUT);
  if (mode === "local") {
    localStorage.setItem(LS_TOKEN, token);
    sessionStorage.removeItem(SS_TOKEN);
  } else {
    sessionStorage.setItem(SS_TOKEN, token);
    localStorage.removeItem(LS_TOKEN);
  }
}

export function clearToken() {
  cancelSessionRenewal();
  localStorage.setItem(SIGNED_OUT, "1");
  localStorage.removeItem(PERSISTENT_SESSION);
  localStorage.removeItem(LS_TOKEN);
  sessionStorage.removeItem(SS_TOKEN);
}

function accessExpiresSoon(token: string) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return !payload.exp || payload.exp * 1000 < Date.now() + 60000;
  } catch { return true; }
}

export async function renewLoginIfNeeded(force = false): Promise<string> {
  const token = getToken();
  if (localStorage.getItem(SIGNED_OUT) === "1") return token;
  if (token && !isLoginRemembered()) return token;
  if (!force && token && !accessExpiresSoon(token)) return token;
  if (renewal) return renewal;
  const epoch = sessionEpoch;
  const pending = (async () => {
    const res = await fetch(joinUrl(RAW_BASE, "/api/auth/session"), { method: "POST", credentials: "include" });
    if (epoch !== sessionEpoch || token !== getToken()) return getToken();
    if (!res.ok) {
      if (res.status === 401) return token;
      throw new Error("로그인 상태를 확인하지 못했어요. 잠시 후 다시 시도해 주세요.");
    }
    const data = await res.json();
    if (epoch !== sessionEpoch || token !== getToken()) return getToken();
    if (!data?.token || data.persistent !== true) throw new Error("잘못된 로그인 응답입니다.");
    markPersistentSession();
    setToken(data.token, "local");
    return data.token as string;
  })();
  renewal = pending;
  try { return await pending; } finally { if (renewal === pending) renewal = null; }
}

// Open a protected document using a fresh token, including after a long idle period.
export async function openAuthenticatedUrl(path: string) {
  const url = new URL(path, window.location.origin);
  const apiOrigin = RAW_BASE ? new URL(RAW_BASE).origin : window.location.origin;
  if (url.origin !== apiOrigin || !url.pathname.startsWith("/api/")) return;
  const popup = window.open("about:blank", "_blank");
  if (popup) popup.opener = null;
  try {
    const token = await renewLoginIfNeeded();
    if (!token) throw new Error("로그인이 필요합니다.");
    url.searchParams.set("token", token);
    if (popup) popup.location.replace(url.toString());
    else window.location.assign(url.toString());
  } catch {
    popup?.close();
    window.alert("파일을 열지 못했어요. 잠시 후 다시 시도해 주세요.");
  }
}

// 공용 fetch: path에는 **반드시** '/api/...' 형태로 넘기세요.
export async function authFetch(input: string, init: RequestInit = {}) {
  // input이 절대 URL이면 그대로 사용, 아니면 BASE와 합치기
  const isAbs = /^https?:\/\//i.test(input);
  const url = isAbs ? input : joinUrl(RAW_BASE, input.replace(/^\/+/, "/"));

  const headers = new Headers(init.headers || {});
  const t = await renewLoginIfNeeded();
  if (t && (!headers.has("Authorization") || /^Bearer /i.test(headers.get("Authorization") || ""))) {
    headers.set("Authorization", `Bearer ${t}`);
  }

  // FormData면 Content-Type을 직접 세팅하지 않음
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  if (isFormData) headers.delete("Content-Type");

  let res = await fetch(url, {
    ...init,
    headers,
    credentials: "include",
  });

  if (res.status === 401 && t && t === getToken() && isLoginRemembered()) {
    const next = await renewLoginIfNeeded(true);
    if (next && next !== t) {
      headers.set("Authorization", `Bearer ${next}`);
      res = await fetch(url, { ...init, headers, credentials: "include" });
    }
  }

  // 공통 401 처리: 로그인 만료 메시지 + 토큰 정리
  if (res.status === 401) {
    try {
      const ct = res.headers.get("content-type") || "";
      if (headers.get("Authorization") === `Bearer ${getToken()}`) clearToken();

      if (ct.includes("application/json")) {
        const j = await res.clone().json().catch(() => null);
        const payload =
          j && typeof j === "object"
            ? { ...j, message: "로그인이 만료되었습니다." }
            : { message: "로그인이 만료되었습니다." };
        const nextHeaders = new Headers(res.headers);
        nextHeaders.set("content-type", "application/json");
        return new Response(JSON.stringify(payload), {
          status: res.status,
          statusText: res.statusText,
          headers: nextHeaders,
        });
      }

      const nextHeaders = new Headers(res.headers);
      nextHeaders.set("content-type", "text/plain; charset=utf-8");
      return new Response("로그인이 만료되었습니다.", {
        status: res.status,
        statusText: res.statusText,
        headers: nextHeaders,
      });
    } catch {
      if (headers.get("Authorization") === `Bearer ${getToken()}`) clearToken();
    }
  }

  return res;
}

// 간단 JSON 헬퍼 (선택)
export async function authJson<T = any>(
  path: string,
  init?: RequestInit
): Promise<T> {
  const r = await authFetch(path, init);
  const j = (await r.json().catch(() => null)) as T;
  return j;
}

/**
 * 1원 계좌 인증 시작
 * POST /api/ekyc/account/verify
 */
export async function startOneWonVerify(payload: {
  bank_code: string;
  account_no: string;
  holder_name: string;
}) {
  const res = await authFetch("/api/ekyc/account/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) {
    throw new Error(json?.message || "1원 인증 시작에 실패했습니다.");
  }
  return json;
}

/**
 * 1원 계좌 인증 확인
 * POST /api/ekyc/account/confirm
 */
export async function confirmOneWonVerify(code: string) {
  const res = await authFetch("/api/ekyc/account/confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });

  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) {
    throw new Error(json?.message || "1원 인증 확인에 실패했습니다.");
  }
  return json;
}

export const API_BASE = RAW_BASE;
